import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axiosInstance from "axiosInstance";
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
// MUI Components
import {
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  List,
  Divider,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  CircularProgress
} from "@mui/material";
import { DeleteOutlined, Edit } from "@mui/icons-material";

const EditPost = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [sections, setSections] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: '',
      summary: '',
      body: '',
      post_type: 0,
      category_id: 0,
    }
  });

  const [post_type, category_id] = watch(["post_type", "category_id"]);

  // Simple ID generator
  const generateId = () => Date.now() + Math.random().toString(36).substr(2, 9);

  // تابع آپلود فایل
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axiosInstance.post('/upload-image/register-advertisement', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data?.data?.success || response.data?.success; 
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file');
      return null;
    }
  };

  // تابع تغییر یافته handleFileUpload برای آپلود واقعی فایل
  const handleFileUpload = async (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const uploadedUrl = await uploadFile(file);
        if (uploadedUrl) {
          setValue(fieldName, uploadedUrl);
          alert('File uploaded successfully!');
        }
      } catch (error) {
        console.error('File upload error:', error);
        alert('Error uploading file');
      } finally {
        setUploading(false);
      }
    }
  };

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.post('/6tduyrduytdfuytcfutyrfc/posts/categories/all', {});
        setCategories(response.data?.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        alert('Error loading categories');
      }
    };
    fetchCategories();
  }, []);

  // Fetch post data
  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      
      try {
        setPostLoading(true);
        const response = await axiosInstance.post("/6tduyrduytdfuytcfutyrfc/posts/edit", { id });
        const postData = response.data?.data;
        
        if (postData) {
          setPost(postData);
          setValue('title', postData.title);
          setValue('summary', postData.summary);
          setValue('body', postData.body);
          setValue('post_type', 0);
          setValue('category_id', postData.category_id);
          
          // Set main image if exists
          if (postData.main_image_url) {
            setValue('main_image_url', postData.main_image_url);
          }
          
          setTags(postData.tags || []);
          setSections(postData.sections_all || []);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        alert('Error loading post data');
      } finally {
        setPostLoading(false);
      }
    };

    fetchPost();
  }, [id, setValue]);

  const onSubmit = async (formData) => {
    if (formData.title === '') {
        alert('Title is required');
        return;
      }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        id: id,
        
        tags: tags.map(tag => tag.id),
        sections: sections.map(section => ({
          id: section.id,
          title: section.title_sections,
          level: section.level,
          content: section.content,
          image_url: section.image_url,
          sections: section.sections ? section.sections.map(subSection => ({
            id: subSection.id,
            title: subSection.title_sections,
            level: subSection.level,
            content: subSection.content,
            image_url: subSection.image_url,
          })) : []
        }))
      };

      const response = await axiosInstance.post('/6tduyrduytdfuytcfutyrfc/posts/update', submitData);
      alert(response.data?.data || 'Post updated successfully!');
      navigate("/blogs");
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Error updating post');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSections = () => {
    const [title_sections, content, image_url, parent, id_s] = getValues([
      "title_sections", "content", "image_url", "parent", "id_s"
    ]);

    if (!id_s) {
      setSections((prev) => {
        if (parent !== undefined && parent !== null) {
          return prev.map((item) => {
            if (item.id !== parent) return item;
            return {
              ...item,
              sections: item.sections ? [
                ...item.sections,
                {
                  id: generateId(),
                  title_sections,
                  level: 'h3',
                  content,
                  image_url,
                  sections: null
                }
              ] : [{
                id: generateId(),
                title_sections,
                level: 'h3',
                content,
                image_url,
                sections: null
              }]
            };
          });
        } else {
          return [
            ...prev,
            {
              id: generateId(),
              title_sections,
              level: 'h2',
              content,
              image_url,
              sections: null
            }
          ];
        }
      });
    } else {
      setSections((prev) => {
        if (parent !== undefined && parent !== null) {
          return prev.map((item) => {
            if (item.id !== parent) return item;
            return {
              ...item,
              sections: item?.sections.map((item2) => {
                if (item2.id !== id_s) return item2;
                return {
                  id: item2.id,
                  title_sections,
                  level: 'h3',
                  content,
                  image_url,
                  sections: null
                };
              })
            };
          });
        } else {
          return prev.map((item) => {
            if (item.id !== id_s) return item;
            return {
              id: item.id,
              title_sections,
              level: 'h2',
              content,
              image_url,
              sections: item.sections
            };
          });
        }
      });
    }

    setOpenDialog(false);
    setValue("title_sections", "");
    setValue("image_url", "");
    setValue("content", "");
    setValue("parent", null);
    setValue("id_s", null);
  };

  const handleDeleteSections = (id, parent) => {
    const filtered = (parent !== undefined && parent !== null) ?
      sections.map((item) => {
        if (item.id !== parent) return item;
        return {
          ...item,
          sections: item.sections ? item.sections.filter((item2) => item2.id !== id) : null
        };
      }) : sections.filter((item) => item.id !== id);
    setSections(filtered);
  };

  // Tag handlers
  const handleAddTag = (tagName) => {
    if (tagName.trim()) {
      const newTag = { id: generateId(), name: tagName.trim() };
      setTags(prev => [...prev, newTag]);
    }
  };

  const handleRemoveTag = (tagId) => {
    setTags(prev => prev.filter(tag => tag.id !== tagId));
  };

  // Show loading while fetching post data
  if (postLoading) {
    return (
        <DashboardLayout>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading post data...</Typography>
      </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <Grid spacing={2} justifyContent="center">
      <Grid item xs={12} sx={{ bgcolor: "#494cec" }}>
        <Typography variant="h4" align="center" sx={{ color: "#fff !important", py: 2 }}>
          Edit Post
        </Typography>
      </Grid>
      <Grid item xs={12} md={10}>
        <Card sx={{ bgcolor: "#fff !important" }}>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                {/* Post Information */}
                <Card>
                  <CardContent>
                    <Typography textAlign="center" variant="h4" color="primary" mb={2}>
                      Post Information
                    </Typography>
                    <TextField
                      fullWidth
                      label="Title"
                      multiline
                      rows={3}
                      {...register("title")}
                      margin="normal"
                    />
                    <TextField
                      fullWidth
                      label="Summary"
                      multiline
                      rows={3}
                      {...register("summary")}
                      margin="normal"
                    />
                    
                    
                  </CardContent>
                </Card>

                {/* Post Body */}
                <Card>
                  <CardContent>
                    <Typography textAlign="center" variant="h4" color="primary" mb={2}>
                      Post Body
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      label="Body"
                      {...register("body", { required: "Body is required" })}
                      error={!!errors.body}
                      helperText={errors.body?.message}
                      margin="normal"
                    />
                  </CardContent>
                </Card>

                {/* Image Upload */}
                <Card>
                  <CardContent>
                    <Typography textAlign="center" variant="h4" color="primary" mb={2}>
                      Main Image
                    </Typography>
                    <Box>
                      {post?.main_image_url && (
                        <img 
                          src={post.main_image_url} 
                          alt="Main" 
                          style={{ maxWidth: '200px', marginBottom: '16px' }}
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, "main_image_url")}
                        disabled={uploading}
                      />
                      {uploading && <Typography variant="body2">Uploading...</Typography>}
                    </Box>
                  </CardContent>
                </Card>

                {/* Tags */}
                <Card style={{display:"none"}}>
                  <CardContent>
                    <Typography textAlign="center" variant="h4" color="primary" mb={2}>
                      Tags
                    </Typography>
                    <TextField
                      fullWidth
                      label="Add Tag"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      margin="normal"
                    />
                    <Box sx={{ mt: 1 }}>
                      {tags.map(tag => (
                        <Box
                          key={tag.id}
                          sx={{
                            display: 'inline-block',
                            bgcolor: 'primary.main',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            m: 0.5
                          }}
                        >
                          {tag.name}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTag(tag.id)}
                            sx={{ color: 'white', ml: 0.5 }}
                          >
                            ×
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>

                {/* Sections */}
                <Card sx={{ textAlign: "center" }}>
                  <CardContent>
                    <Typography variant="h4" color="primary" mb={2}>
                      Sections
                    </Typography>
                    <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                      {sections.map((item) => (
                        <Box key={item.id} sx={{ border: "2px dashed #00000040", mb: 2 }}>
                          <ListItem
                            secondaryAction={
                              <>
                                <IconButton
                                  edge="end"
                                  onClick={() => handleDeleteSections(item.id, null)}
                                >
                                  <DeleteOutlined />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  onClick={() => {
                                    setOpenDialog(true);
                                    setValue("title_sections", item?.title_sections);
                                    setValue("image_url", item?.image_url);
                                    setValue("content", item?.content);
                                    setValue("parent", null);
                                    setValue("id_s", item?.id);
                                  }}
                                >
                                  <Edit />
                                </IconButton>
                              </>
                            }
                          >
                            {item.image_url && (
                              <img
                                src={item.image_url}
                                width={100}
                                height={100}
                                alt="section"
                                style={{ marginRight: 16 }}
                              />
                            )}
                            <ListItemText
                              primary={
                                <Box>
                                  <Typography variant="h6">Title: {item.title_sections}</Typography>
                                  <Typography variant="body2">Content: {item.content}</Typography>
                                </Box>
                              }
                            />
                          </ListItem>

                          <CardContent>
                            <Button
                              variant="contained"
                              color="secondary"
                              sx={{color: "#fff !important"}}
                              onClick={() => {
                                setValue("parent", item?.id);
                                setValue("title_sections", "");
                                setValue("image_url", "");
                                setValue("content", "");
                                setValue("id_s", null);
                                setOpenDialog(true);
                              }}
                            >
                              Add Child Sections
                            </Button>
                          </CardContent>

                          {item.sections && item.sections.length > 0 && (
                            <Card sx={{ textAlign: "center", bgcolor: "#ebb3" }}>
                              <CardContent>
                                <Typography variant="h5" color="primary" mb={2}>
                                  Child Sections of {item.title_sections}
                                </Typography>
                                <List>
                                  {item.sections.map((item2) => (
                                    <ListItem
                                      key={item2.id}
                                      secondaryAction={
                                        <>
                                          <IconButton
                                            edge="end"
                                            onClick={() => handleDeleteSections(item2.id, item.id)}
                                          >
                                            <DeleteOutlined />
                                          </IconButton>
                                          <IconButton
                                            edge="end"
                                            onClick={() => {
                                              setOpenDialog(true);
                                              setValue("title_sections", item2.title_sections);
                                              setValue("image_url", item2.image_url);
                                              setValue("content", item2.content);
                                              setValue("parent", item.id);
                                              setValue("id_s", item2.id);
                                            }}
                                          >
                                            <Edit />
                                          </IconButton>
                                        </>
                                      }
                                    >
                                      {item2.image_url && (
                                        <img
                                          src={item2.image_url}
                                          width={80}
                                          height={80}
                                          alt="child section"
                                          style={{ marginRight: 16 }}
                                        />
                                      )}
                                      <ListItemText
                                        primary={
                                          <Box>
                                            <Typography variant="h6">Title: {item2.title_sections}</Typography>
                                            <Typography variant="body2">Content: {item2.content}</Typography>
                                          </Box>
                                        }
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </CardContent>
                            </Card>
                          )}
                          <Divider variant="inset" />
                        </Box>
                      ))}
                    </List>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{color: "#fff !important"}}
                      onClick={() => {
                        setValue("title_sections", "");
                        setValue("image_url", "");
                        setValue("content", "");
                        setValue("parent", null);
                        setValue("id_s", null);
                        setOpenDialog(true);
                      }}
                    >
                      Add Sections
                    </Button>
                  </CardContent>
                </Card>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{ color: "#fff !important",mt: 2 }}
                >
                  {loading ? "Updating..." : "Update Post"}
                </Button>
              </Stack>
            </form>

            {/* Add Section Dialog */}
            <Dialog onClose={() => setOpenDialog(false)} open={openDialog}>
              <DialogTitle>Add Section</DialogTitle>
              <DialogContent>
                <Box sx={{ minWidth: 300, pt: 1 }}>
                  <TextField
                    fullWidth
                    label="Title"
                    {...register("title_sections")}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Content"
                    {...register("content")}
                    margin="normal"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, "image_url")}
                    style={{ margin: '16px 0' }}
                    disabled={uploading}
                  />
                  {uploading && <Typography variant="body2">Uploading image...</Typography>}
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={handleAddSections}
                    sx={{ color: "#fff !important",mt: 1 }}
                  >
                    Save Section
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    </DashboardLayout>
  );
};

export default EditPost;